import { cn } from "@/lib/utils";
import * as React from "react";

export interface SuggestionListProps<T> {
  children: React.ReactElement<{
    onKeyDown?: (e: React.KeyboardEvent) => void;
    onFocus?: (e: React.FocusEvent) => void;
    onBlur?: (e: React.FocusEvent) => void;
  }>;
  suggestions: T[];
  renderSuggestion: (suggestion: T) => React.ReactNode;
  onSuggestionSelect: (suggestion: T) => void;
  getSuggestionKey: (suggestion: T) => string | number;
  className?: string;
  /**
   * When true, the list is shown only while the input has focus and there are
   * results. Blur closes it so the list does not cover fields below the input.
   */
  closeOnBlur?: boolean;
}

export function SuggestionList<T>({
  children,
  suggestions,
  renderSuggestion,
  onSuggestionSelect,
  getSuggestionKey,
  className,
  closeOnBlur = false,
}: SuggestionListProps<T>) {
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [isInputFocused, setIsInputFocused] = React.useState(false);
  const [focusedIndex, setFocusedIndex] = React.useState(-1);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);

  // Show suggestions if we have any
  React.useEffect(() => {
    setShowSuggestions(suggestions.length > 0);
    setFocusedIndex(-1); // Reset focus when suggestions change
  }, [suggestions]);

  // Handle click outside to close suggestions
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelect = (suggestion: T) => {
    onSuggestionSelect(suggestion);
    setShowSuggestions(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusedIndex((prev) => {
          const next = prev + 1;
          return next >= suggestions.length ? 0 : next;
        });
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex((prev) => {
          const next = prev - 1;
          return next < 0 ? suggestions.length - 1 : next;
        });
        break;
      case "Enter":
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < suggestions.length) {
          handleSelect(suggestions[focusedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setShowSuggestions(false);
        break;
    }
  };

  // Scroll focused item into view
  React.useEffect(() => {
    if (focusedIndex >= 0 && listRef.current) {
      const focusedItem = listRef.current.children[focusedIndex] as HTMLElement;
      if (focusedItem) {
        focusedItem.scrollIntoView({ block: "nearest" });
      }
    }
  }, [focusedIndex]);

  // Clone the child element to add keyboard event handlers
  const childWithProps = React.cloneElement(children, {
    onKeyDown: handleKeyDown,
    ...(closeOnBlur
      ? {
          onFocus: (event: React.FocusEvent) => {
            children.props.onFocus?.(event);
            setIsInputFocused(true);
            if (suggestions.length > 0) {
              setShowSuggestions(true);
            }
          },
          onBlur: (event: React.FocusEvent) => {
            children.props.onBlur?.(event);
            setIsInputFocused(false);
          },
        }
      : {}),
  });

  const suggestionsOpen = showSuggestions && (!closeOnBlur || isInputFocused);

  return (
    <div ref={containerRef} className="relative">
      {childWithProps}
      {suggestionsOpen && (
        <div
          className={cn(
            "absolute z-50 w-full mt-1 bg-popover text-popover-foreground rounded-md border shadow-md",
            className,
          )}
          // Keep focus on the input so choosing a result does not count as blur.
          onMouseDown={
            closeOnBlur
              ? (event) => {
                  event.preventDefault();
                }
              : undefined
          }
        >
          <ul ref={listRef} className="max-h-[300px] overflow-y-auto">
            {suggestions.map((suggestion, index) => (
              // biome-ignore lint/a11y/useKeyWithClickEvents: Keyboard navigation is handled at container level
              <li
                key={getSuggestionKey(suggestion)}
                className={cn(
                  "cursor-pointer px-2 py-1.5 text-sm outline-none hover:bg-primary hover:text-primary-foreground",
                  focusedIndex === index &&
                    "bg-primary text-primary-foreground",
                )}
                onClick={() => handleSelect(suggestion)}
              >
                {renderSuggestion(suggestion)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
